import React, {ReactElement, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {APP, DEFAULT_BLACK_COLOR} from 'src/style/variable';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import './InsurancePlanDetailsTable';
import HelperUtils from '@shared/utils/HelperUtils';
import DownArrowIcon from '@components/system/MyIcons/DownArrowIcon';
import UpArrowIcon from '@components/system/MyIcons/UpArrowIcon';
import BenefitInformation from './BenefitInformation';
import {Plan} from '../EligibilityVerificationMapper';
import {PlanHeaderStyle} from 'src/modules/admin/modules/Invoices/TableStyles';

interface InsurancePlanDetailsTableInterface {
  plans: Plan[];
  expandFirstPlan?: boolean;
}

const InsurancePlanDetailsTable = (
  props: InsurancePlanDetailsTableInterface
): ReactElement => {
  const {plans, expandFirstPlan = false} = props;
  const {t} = useTranslation();

  const tableWrapperRef = useRef<any>();

  useEffect(() => {
    // Expand the first plan by default
    if (tableWrapperRef.current && expandFirstPlan) {
      const firstPlanRow = document.querySelector<HTMLTableRowElement>(
        'tbody tr'
      );
      if (firstPlanRow) {
        firstPlanRow?.click?.();
      }
    }
  }, [expandFirstPlan]);

  const renderDownArrowIcon = (): ReactElement => {
    return <DownArrowIcon id="downArrow" name="downArrow" />;
  };

  const renderUpArrowIcon = (): ReactElement => {
    return <UpArrowIcon id="upArrow" name="upArrow" />;
  };

  return (
    <div className={`section-header insurance-table-details`}>
      <div className="header-label">{t('ELIGIBILITY_VERIFICATION.PLANS')}</div>
      <div>
        <GridTable
          columns={[
            {
              title: t('ELIGIBILITY_VERIFICATION.PLAN_NAME'),
              field: 'planName',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return <div>{rowData?.planName ? rowData.planName : '-'}</div>;
              }
            },
            {
              title: t('ELIGIBILITY_VERIFICATION.STATUS'),
              field: 'status',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return <div>{rowData?.status ? rowData.status : '-'}</div>;
              }
            },
            {
              title: t('ELIGIBILITY_VERIFICATION.POLICY_NUMBER'),
              field: 'policyNumber',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return (
                  <div>
                    {rowData?.policyNumber ? rowData.policyNumber : '-'}
                  </div>
                );
              }
            },
            {
              title: t('ELIGIBILITY_VERIFICATION.PLAN_START_DATE'),
              field: 'planStartDate',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return (
                  <div>
                    {rowData?.planStartDate
                      ? HelperUtils.formatDisplayDate(rowData.planStartDate)
                      : '-'}
                  </div>
                );
              }
            },
            {
              title: t('ELIGIBILITY_VERIFICATION.PLAN_END_DATE'),
              field: 'planEndDate',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return (
                  <div>
                    {rowData?.planEndDate
                      ? HelperUtils.formatDisplayDate(rowData.planEndDate)
                      : '-'}
                  </div>
                );
              }
            },
            {
              title: t('ELIGIBILITY_VERIFICATION.PROVIDER_NAME'),
              field: 'provider_name',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return (
                  <div>
                    {rowData?.primaryCareProvider?.name
                      ? rowData.primaryCareProvider.name
                      : '-'}
                  </div>
                );
              }
            },
            {
              title: t('ELIGIBILITY_VERIFICATION.PROVIDER_NPI'),
              field: 'provider_npi',
              cellStyle: {
                ...PlanHeaderStyle
              },
              headerStyle: {
                ...PlanHeaderStyle
              },
              sorting: false,
              render: (rowData: Plan) => {
                return (
                  <div>
                    {rowData?.primaryCareProvider?.npi
                      ? rowData.primaryCareProvider.npi
                      : '-'}
                  </div>
                );
              }
            }
          ]}
          data={plans}
          options={{
            ...DEFAULT_GRID_TABLE__OPTIONS,
            emptyRowsWhenPaging: false,
            sorting: true,
            paging: false,
            showEmptyDataSourceMessage: true,
            maxBodyHeight: '100%',
            actionsColumnIndex: -1,
            tableLayout: 'auto',
            detailPanelColumnAlignment: 'left',
            // selection: request,
            draggable: false,
            // showSelectAllCheckbox: request,
            headerStyle: {
              color: DEFAULT_BLACK_COLOR,
              fontFamily: APP.FONT_FAMILY.MEDIUM,
              flexDirection: 'row',
              zIndex: 100
            }
          }}
          detailPanel={[
            {
              tooltip: t('ELIGIBILITY_VERIFICATION.CLICK_TO_VIEW_BENEFITS'),
              icon: renderDownArrowIcon,
              openIcon: renderUpArrowIcon,
              render: (rowData: Plan) => (
                // If plan is expanded by default, then expand the benefit as well
                <BenefitInformation
                  data={rowData.benefits}
                  expandFirstBenefit={expandFirstPlan}
                />
              )
            }
          ]}
          onRowClick={(event: any, rowData: any, togglePanel) => {
            togglePanel?.();
          }}
          style={{
            margin: '0rem',
            marginBottom: '2rem',
            marginTop: '0rem',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow:
              '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)'
          }}
          tableRef={tableWrapperRef}
        />
      </div>
    </div>
  );
};

export default InsurancePlanDetailsTable;
